<script setup>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import { DATE_SORT, CANONICAL_SORT, ALPHABET_SORT, } from '@/assets/js/src/util/uiTools'
import { getActivePinia, storeToRefs, } from 'pinia'
import { useLangStore, } from '@/assets/js/src/modules/lang/_pinia/lang'
import { useResourceListStore, } from '@/assets/js/src/modules/resource/_pinia/resourceList'
import { useResourceStore, } from '@/assets/js/src/modules/resource/_pinia/resource'
import BibleBookFilter from '@/assets/js/src/components/forms/BibleBookFilter.vue'
import { useVideoListFilter, } from '@/assets/js/src/modules/resource/_composables/useVideoListFilter'
import SortOptions from '@/assets/js/src/components/forms/SortOptions.vue'
import ResourceAbbrivationFilter from '@/assets/js/src/components/forms/ResourceAbbrivationFilter.vue'
import SearchFilter from '@/assets/js/src/components/forms/SearchFilter.vue'
import H1Title from '@/assets/js/src/components/overlay/H1Title.vue'
import SearchHitCount from '@/assets/js/src/components/forms/SearchHitCount.vue'
import GridListContent from '@/assets/js/src/components/overlay/GridListContent.vue'
import { computed, } from 'vue'
import { useAnalytics, } from '@/assets/js/src/util/composables/useAnalytics'

defineProps(
    {
        asyncStatus: {
            type: Number,
            default: 1,
        },
    }
)

let activePinia = getActivePinia()

const resourceListStore = useResourceListStore(activePinia)
const {
    resourceItems,
    page,
    maxPages,
    count,
    availableBooks,
} = storeToRefs(resourceListStore)

const resourceStore = useResourceStore(activePinia)
const {
    resources,
} = storeToRefs(resourceStore)

const langStore = useLangStore(activePinia)
const {
    guiLanguage,
} = storeToRefs(langStore)

const { bsa, } = useAnalytics()
const getMoreResults = async function () {
    if (page.value === maxPages.value) {
        return
    }
    let next = page.value + 1
    await resourceListStore.getResourceItems({ page: next, provider: 'video', })

    bsa?.event({
        eventCategory: 'load more',
        eventAction: 'Videos nachladen',
        eventLabel: next,
    })
}

const sortEnum = [ DATE_SORT, CANONICAL_SORT, ALPHABET_SORT, ]

const abbreviationsEnum = computed(() =>{
    return Object.values(resources.value)
        .reduce((acc, resource) => {
            let abbreviations = acc.map(resource => resource.abbreviation)
            if(resource.provider.split('_')[0] === 'video' && !abbreviations.includes(resource.abbreviation)) {
                acc.push(resource)
            }

            return acc
        },[])
        .map((resource) => ({
            title: resource.name,
            value: resource.abbreviation,
        }))
})

const resourceNames = computed(() => {
    return resourceItems.value.map(item => {
        let resource = resources.value[item.resource.id]
        let name = resource.name
        if (guiLanguage.value.id !== resource?.language?.id) {
            name += resource?.language?.international_name ? ` (${resource?.language?.international_name})` : ''
        }

        return name
    })
})

const {
    debouncedSearch,
    abbreviations,
    books,
    sort,
    DIR_RTL,
    DIR_LTR,
} = useVideoListFilter()
</script>

<template>
    <overlay
        class-name="video-list"
        full-width
    >
        <v-row no-gutters>
            <v-col
                cols="12"
                md="6"
                offset-md="3"
            >
                <h1-title title-key="async.resource.video.list.title" />

                <bible-book-filter
                    v-model="books"
                    :available-books="availableBooks"
                ></bible-book-filter>

                <resource-abbrivation-filter
                    v-model="abbreviations"
                    :items="abbreviationsEnum"
                    label-key="async.resource.video.list.filter"
                ></resource-abbrivation-filter>

                <search-filter
                    v-model="debouncedSearch"
                    label-key="async.resource.video.list.filter_placeholder"
                ></search-filter>

                <div class="d-flex align-center mb-7">
                    <search-hit-count
                        :count="count"
                    ></search-hit-count>

                    <v-spacer></v-spacer>

                    <sort-options
                        v-model="sort"
                        :types="sortEnum"
                    ></sort-options>
                </div>
            </v-col>
        </v-row>

        <!-- GRID-ANSICHT -->
        <grid-list-content
            :items="resourceItems"
            :async-status="asyncStatus"
            :page="page"
            :max-pages="maxPages"
            no-items-key="async.resource.video.list.no_filtered_items"
            @load-more="getMoreResults"
        >
            <template #item="{item: videoItem, index,}">
                <router-link
                    :class="[{'first': index === 0}, 'video-list--grid__item', 'elevation-1']"
                    :dir="resources[videoItem.resource.id].language.rtl ? DIR_RTL : DIR_LTR"
                    :to="{path:`/video/${encodeURIComponent(resources[videoItem.resource.id].abbreviation)}/${encodeURIComponent(videoItem.title.replace('/',''))}/${videoItem.id}`}"
                >
                    <div
                        class="video-list--grid__item--limiter"
                    >
                        <bs-v-img
                            :src="((videoItem.metadata || {}).thumb || videoItem.image)"
                            :lazy-src="(videoItem.metadata || {}).lazy_thumb"
                            :alt="videoItem.title"
                            :aspect-ratio="16/9"
                            :class="['secondary--text']"
                        >
                            <template #placeholder>
                                <v-row
                                    class="fill-height ma-0"
                                    align="center"
                                    justify="center"
                                >
                                    <v-progress-circular
                                        indeterminate
                                        :class="['secondary--text']"
                                    ></v-progress-circular>
                                </v-row>
                            </template>
                        </bs-v-img>
                        <h2 class="text-truncate">
                            {{ videoItem.title }}
                        </h2>
                        <p class="text-truncate video-list--grid__item__resource-name">
                            {{ resourceNames[index] }}
                        </p>
                        <div
                            v-if="((videoItem.metadata || {}).thumb || videoItem.image)"
                            class="d-flex justify-center align-center video-list--grid__item__play-btn"
                        >
                            <v-icon>bs:$vuetify.icons.mdiPlayCircleOutline</v-icon>
                        </div>
                    </div>
                </router-link>
            </template>
        </grid-list-content>
    </overlay>
</template>

<style lang="scss">
.bs-app {
    .video-list {
        color: var(--bs-color-black);

        &--grid {
            &__item {
                display: block;
                margin: map-deep-get($bs-xl, overlay, grid, margin);
                overflow: hidden;
                text-decoration: none;
                background-color: var(--bs-color-white);

                h2 {
                    margin: 8px 16px;
                    color: var(--bs-color-black);
                    font-weight: 500;
                    font-size: 18px;
                }

                &:hover, &:focus {
                    h2 {
                        font-weight: 700;
                    }
                }

                &__resource-name {
                    margin: 0 16px 16px;
                    color: map-deep-get($bs-color, autocomplete, font);
                    font-size: 12px;
                }

                &--limiter {
                    position: relative;

                    .v-icon {
                        width: 48px;
                        height: 48px;
                        color: map-deep-get($bs-color, white);
                        filter: drop-shadow(0 0 4px black);
                    }
                }

                &__play-btn {
                    position: absolute;
                    top: 0;
                    width: 100%;
                    height: 68%;
                    cursor: pointer;
                }
            }
        }


    }
}
</style>
